/* eslint-disable react/prop-types */
import React, {useEffect, useState} from "react";
import {Container, IconButton, Box, Button, CircularProgress, Typography, useMediaQuery} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import {makeStyles, Theme, useTheme} from "@material-ui/core/styles";
import {useSelector, useDispatch} from "react-redux";
import {
    getChat,
    setMessage,
    getMessages,
    getMessagesCount,
    setExternalMessage,
    uploadImage,
    updateTypeIndicator,
    sendPushNotification,
    getInter
} from "../../redux/chat/chat.actions";
import {RootState, useAppDispatch} from "../../redux/store";
import MessageList from "./MessageList";
import MessageForm from "./MessageForm";
import PusherChat from "../../helpers/chatHelper";
import {useHistory} from "react-router-dom";
import {useSnackbar} from "notistack";
import moment from "moment-timezone";
import {patchAppointment} from "../../redux/appointment/appointment.actions";
import {getUserLocation} from "../../redux/user/user.actions";
import publicIp from "public-ip";
import deviceChecker from "../../utils/deviceChecker";

//Styles:
const useStyle = makeStyles((theme: Theme) => ({
    icon: {
        position: "fixed",
        right: "50px",
        top: "50px"
    },
    mainButton: {
        position: "fixed",
        zIndex: 10,
        top: "16px",
        right: "20vw"
    }
}));

const vetStyle = makeStyles((theme: Theme) => ({
    icon: {
        position: "relative",
        paddingTop: "16px"
    },
    mainButton: {
        paddingTop: "0px",
        float: "right",
        paddingRight: "25px",
    },
}));

const getUserName = (user, defaultName) => {
    if (user && user.firstName && user.lastName) {
        if (user.type === "vet") {
            return `Dr. ${user.lastName}`;
        }
        if (user.type === "tech") {
            return user.firstName;
        }
        return `${user.firstName} ${user.lastName}`;
    } else {
        return defaultName;
    }
};

const Chat = (props: {
    data: any,
    vet: any,
    appointmentId: number,
    over: any,
    closeChat: (open, data, over) => void,
    closeTranscript: () => void,
    isVet: boolean,
    isTranscript: boolean,
    isVideo?: boolean,
    chatClosed?: null | string,
    setVideoToken?: (any) => void,
    setDisplayChat?: (any) => void,
    handleClose?: () => void,
    isJoin?: string,
}) => {
    // initial
    const {enqueueSnackbar} = useSnackbar();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const reduxChat = useSelector((state: RootState) => state?.chat);
    const user = useSelector((state: RootState) => state?.chat?.interlocutor);
    const originalUser = useSelector((state: RootState) => state?.user);
    const appointmentData = useSelector((state: RootState) => state?.appointment.appointment);

    // const
    const {
        data,
        vet,
        over,
        closeChat,
        closeTranscript,
        isVet,
        appointmentId,
        isTranscript,
        isVideo,
        chatClosed,
        setVideoToken,
        setDisplayChat,
        handleClose,
        isJoin
    } = props;
    const chatId = reduxChat.channel?.id;
    const classes = useStyle();
    const vetClasses = vetStyle();
    const theme = useTheme();

    // state
    const [chat, setChat] = useState(null);
    const [userTyping, setUserTyping] = useState("");
    const [isFinished, setIsFinished] = useState(null);
    const [timePassed, setTimePassed] = useState(false);
    const [saving, setSaving] = useState(false);

    // vet / user ids
    const clientUserId = data?.clientUserId;
    const vetUserId = data?.vetUserId;
    const userID = originalUser.userId;
    const interlocutorID = userID === clientUserId ? vetUserId : clientUserId;

    // vet / user names
    const doctorName = getUserName(vet, "Veterinarian");
    const patientName = getUserName(user, "Pet owner");
    const names = {
        [vetUserId]: doctorName,
        [clientUserId]: patientName
    };

    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

    const checkIfTimePassed = () => {
        const scheduledTime = moment(appointmentData.scheduledDate);
        const timeDiff = scheduledTime.diff(moment(), "minutes");
        if (timeDiff < 0) {
            if (Math.abs(timeDiff) >= 60) {
                setTimePassed(true);
                setIsFinished(true);
            }
        }
    };

    const requestAppointment = () => {
        // eslint-disable-next-line no-prototype-builtins
        appointmentData.hasOwnProperty("finished") ? setIsFinished(appointmentData.finished) : setIsFinished(false);
    };

    useEffect(() => {
        dispatch(getChat({appointmentId: data?.appointmentId || data?.id}));
        user?.userId !== clientUserId && dispatch(getInter({userId: clientUserId}));
        requestAppointment();
        checkIfTimePassed();
    }, []);

    useEffect(() => {
        if (chatClosed && !isVideo) {
            if (!isVet) {
                handleChatData();
            } else {
                handleClick();
            }
            if ((!isVet || originalUser.role === "OWNER")) {
                isVideo && handleClose();
                history.push({pathname: "/apointmentRate", state: {appointmentId: appointmentId}});
            }
            else {
                isVideo && handleClose();
                history.push({
                    pathname: "/apointmentPrescriptions",
                    state: {appointmentId: appointmentId}
                });
            }
        }
        if (chatClosed && isVideo) {
            if (!isVet) {
                handleChatData();
            } else {
                handleClick();
            }
            if (chatClosed === "logout") {
                (async () => {
                    if (originalUser.role !== "OWNER") {
                        const tz = moment.tz.guess();
                        const time = moment.tz(moment(), tz);
                        await dispatch(patchAppointment(appointmentId, {actualEndTime: time.format(), finished: true}));
                    }
                })();
                setVideoToken(null);
            }
            if ((!isVet || originalUser.role === "OWNER")) {
                isVideo && handleClose();
                history.push({pathname: "/apointmentRate", state: {appointmentId: appointmentId}});
            }
            else {
                isVideo && handleClose();
                history.push({
                    pathname: "/apointmentPrescriptions",
                    state: {appointmentId: appointmentId}
                });
            }
        }

    }, [chatClosed]);

    useEffect(() => {
        if (reduxChat.error) {
            if (!chatId) {
                enqueueSnackbar("An error occurred: no chat was found for this appointment.", {variant: "error"});
            }
        }
    }, [reduxChat.error]);

    useEffect(() => {
        if (reduxChat.isFinishedError) {
            requestAppointment();
        }
    }, [reduxChat.isFinishedError]);

    useEffect(() => {
        (async () => {
            if (chatId && user) {
                dispatch(getMessages({chatId}));
                dispatch(getMessagesCount({chatId}));
                const chat = PusherChat.subscribeChannel(chatId);
                if (chat) {
                    if (!over) {
                        if (originalUser.role === "OWNER") {
                            const ip = await publicIp.v4();
                            const userLocation = await dispatch(getUserLocation(ip));
                            dispatch(patchAppointment(appointmentId, {
                                zipCode: userLocation.zip,
                                device: deviceChecker()
                            }));
                        } else {
                            const tz = moment.tz.guess();
                            const time = moment.tz(moment(), tz);
                            dispatch(patchAppointment(appointmentId, {
                                actualStartTime: time.format()
                            }));
                        }
                        isJoin?.length && addMessage(`${names[userID] || "New member"} has joined the channel.`);
                    }
                    setChat(chat);
                    subscriber(chat);
                    return () => unSubscriber(chat);
                }
            }
        })();
    }, [chatId, user]);

    const subscriber = pusherChat => {
        pusherChat.bind(PusherChat.EVENT_MESSAGE, async function (data) {
            if (data.message === "Chat is no longer active. Please request a new chat appointment if you need additional advice. Thanks!") {
                setIsFinished(true);
                const tz = moment.tz.guess();
                const time = moment.tz(moment(), tz);
                if (originalUser.role !== "OWNER") {
                    await dispatch(patchAppointment(appointmentId, {
                        finished: true,
                        actualEndTime: time.format()
                    }));
                }
                setTimeout(() => {
                    if (originalUser.role === "OWNER") {
                        isVideo && handleClose();
                        history.push({pathname: "/apointmentRate", state: {appointmentId: appointmentId}});
                    }
                }, 500);
            }
            if (data.message === "The veterinary provider has re-opened the chat.") {
                setIsFinished(false);
            }
            dispatch(setExternalMessage(data));
        });
        pusherChat.bind(PusherChat.EVENT_TYPING, function (data) {
            if (data?.isTyping && data?.authorId !== userID) {
                setUserTyping(names[data?.authorId]);
            } else {
                setUserTyping("");
            }
        });
        pusherChat.bind(PusherChat.EVENT_NOTIFICATION, function (data) {
            dispatch(setExternalMessage(data));
        });
        //window.addEventListener("beforeunload", handleChatData);
    };

    const unSubscriber = pusherChat => {
        PusherChat.unsubscribeChannel(chatId);
        if (pusherChat) {
            pusherChat.unbind(PusherChat.EVENT_MESSAGE);
            pusherChat.unbind(PusherChat.EVENT_TYPING);
            pusherChat.unbind(PusherChat.EVENT_NOTIFICATION);
        }
    };

    const userType = (typing) => {
        dispatch(updateTypeIndicator({
            chatId,
            body: {
                authorId: userID,
                eventName: PusherChat.EVENT_TYPING,
                isTyping: typing,
                typing,
            }
        }));
    };

    const addMessage = async (text) => {
        if (interlocutorID) {
            await dispatch(sendPushNotification({
                userID: interlocutorID,
                body: {
                    title: "New message",
                    message: text
                }
            }));
        }
        dispatch(setMessage({
            chatId,
            body: {
                authorId: userID,
                eventName: PusherChat.EVENT_MESSAGE,
                message: text,
                created: new Date(),
            }
        }));
    };

    const addMediaMessage = async file => {
        if (chat) {
            setSaving(true);
            const reader = new FileReader();
            reader.onload = () => {
                dispatch(uploadImage({
                    chatId,
                    file: reader.result,
                    body: {
                        authorId: userID,
                        eventName: PusherChat.EVENT_MESSAGE,
                        message: "",
                        created: new Date(),
                    }
                }));
                setSaving(false);
            };
            reader.readAsArrayBuffer(file);
        }
    };

    const handleChatData = () => {
        unSubscriber(chat);
        if (!over) {
            addMessage(`${names[userID] || "New"} member left`);
        }
        closeChat(false, {}, true);
    };

    const handleCloseTranscript = () => {
        //this is breaking past
        closeTranscript();
    };

    const handleClick = async () => {
        setIsFinished(!isFinished);
        if (!isFinished) {
            if (chat) {
                const text = "Chat is no longer active. Please request a new chat appointment if you need additional advice. Thanks!";
                addMessage(text);
            }
        } else {
            if (chat) {
                await dispatch(patchAppointment(appointmentId, {"finished": false}));
                const text = "The veterinary provider has re-opened the chat.";
                addMessage(text);
            }
        }
    };

    return (
        <Box>
            <>
                {
                    originalUser.role !== "OWNER" && !over && !timePassed && !isVideo &&
                    <Box className={isVet ? vetClasses.mainButton : classes.mainButton}>
                        <Button color="primary" variant="contained" onClick={handleClick.bind(null)}>
                            {isFinished ? "Open Chat" : "End Chat"}
                        </Button>
                    </Box>
                }
                {!isVet && !isVideo
                &&
                <IconButton
                    edge="start"
                    color="inherit"
                    onClick={isTranscript && over ? () => handleCloseTranscript() : handleChatData.bind(null, false, {})}
                    aria-label="close"
                    className={isVet ? vetClasses.icon : classes.icon}
                >
                    <CloseIcon fontSize="large"/>
                </IconButton>
                }
                <Container className="video-chat-container" >
                    <MessageList messages={reduxChat.messages} names={names} isVet={isVet} isVideo={isVideo}/>
                    {!over && !saving &&
                    <MessageForm
                        handleSubmit={addMessage}
                        handleAddMedia={addMediaMessage}
                        disabled={!chat || isFinished}
                        userTyping={userTyping}
                        startTyping={() => userType(true)}
                        endTyping={() => userType(false)}
                        isVet={isVet}
                        isVideo={isVideo}
                    />
                    }
                    {saving &&
                    <Box width="100%" height="150px" display="flex" justifyContent="center" alignItems="center">
                        <CircularProgress/>
                    </Box>
                    }
                </Container>
            </>
        </Box>
    );
};

export default Chat;

// LOADER
// <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100vh">
// 						<CircularProgress size={100}/> </Box>
